import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BookThumb, InsightCard } from '@/components/insights'
import { useMyLoans } from '@/hooks/loans'
import type { LibraryBook } from '@/lib/insights'
import { parseDate } from '@/lib/utils/date'

interface LoanRow {
  key: string
  title: string
  cover: string | null | undefined
  person: string | null
  since: string | null
}

/** Books borrowed from others that are still with you, and your books lent out */
export function LoansCard({ books }: { books: LibraryBook[] }) {
  const { t, i18n } = useTranslation()
  const { data: loans = [] } = useMyLoans()

  const { withMe, lentOut } = useMemo(() => {
    const byId = new Map(books.map((b) => [b.id, b]))
    const active = loans.filter((l) => !l.returned_on)
    const activeCopies = new Set(active.map((l) => l.borrower_book_id))
    const returnedCopies = new Set(
      loans.filter((l) => l.returned_on).map((l) => l.borrower_book_id)
    )

    const withMe: LoanRow[] = [
      // Lent through the app
      ...active
        .filter((l) => l.borrower_book_id && byId.has(l.borrower_book_id))
        .map((l) => {
          const book = byId.get(l.borrower_book_id as string)!
          return {
            key: l.id,
            title: book.title,
            cover: book.cover_url,
            person: l.lender_name,
            since: l.lent_on,
          }
        }),
      // Marked as borrowed by hand (someone outside the app); gone once read
      ...books
        .filter(
          (b) =>
            b.ownership === 'borrowed' &&
            b.status !== 'read' &&
            !activeCopies.has(b.id) &&
            !returnedCopies.has(b.id)
        )
        .map((b) => ({
          key: b.id,
          title: b.title,
          cover: b.cover_url,
          person: b.borrowed_from ?? null,
          since: null,
        })),
    ]

    const lentOut: LoanRow[] = active
      .filter((l) => byId.has(l.book_id))
      .map((l) => {
        const book = byId.get(l.book_id)!
        return {
          key: l.id,
          title: book.title,
          cover: book.cover_url,
          person: l.borrower_name,
          since: l.lent_on,
        }
      })

    return { withMe, lentOut }
  }, [books, loans])

  if (withMe.length === 0 && lentOut.length === 0) return null

  const format = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' })

  const renderList = (rows: LoanRow[], labelKey: 'loans.from' | 'loans.to') => (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li key={row.key} className="flex items-center gap-3">
          <BookThumb src={row.cover} title={row.title} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{row.title}</p>
            <p className="truncate text-xs text-muted-foreground">
              {row.person ? t(labelKey, { name: row.person }) : t('loans.borrowed')}
              {row.since && ` · ${t('loans.since', { date: format.format(parseDate(row.since)) })}`}
            </p>
          </div>
        </li>
      ))}
    </ul>
  )

  return (
    <InsightCard icon="🤝" title={t('loans.cardTitle')}>
      <div className="space-y-4">
        {withMe.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              📥 {t('loans.withMe', { count: withMe.length })}
            </p>
            {renderList(withMe, 'loans.from')}
          </div>
        )}
        {lentOut.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              📤 {t('loans.lentOut', { count: lentOut.length })}
            </p>
            {renderList(lentOut, 'loans.to')}
          </div>
        )}
      </div>
    </InsightCard>
  )
}
